import { useEffect, useState } from 'react';
import {
  addSeconds,
  differenceInSeconds,
  formatDuration,
  intervalToDuration,
} from 'date-fns';

interface UseStreamEndNotificationProps {
  sessionId: string;
  sessionStartTime?: Date;
  maxTtl?: number; // Maximum session duration in seconds
  notificationTime?: number; // When to show notification (seconds before end)
  notificationDuration?: number; // How long to show notification (seconds)
}

/**
 * Hook that shows notification when streaming session is about to end
 * Based on ov-dgxc-portal-sample implementation
 */
export function useStreamEndNotification({
  sessionId,
  sessionStartTime = new Date(),
  maxTtl = 28800, // 8 hours default
  notificationTime = 600, // 10 minutes default
  notificationDuration = 30, // 30 seconds default
}: UseStreamEndNotificationProps) {
  const [notificationSent, setNotificationSent] = useState<boolean>(false);

  useEffect(() => {
    if (!sessionId || notificationSent) {
      return;
    }

    const timer = setInterval(() => {
      const now = Date.now();
      const endTime = addSeconds(sessionStartTime, maxTtl);
      const diff = differenceInSeconds(endTime, now);

      if (diff - notificationTime <= 0) {
        const remainingDuration = formatDuration(
          intervalToDuration({ start: now, end: endTime }),
          {
            format: ['minutes', 'seconds'],
            zero: false,
          }
        );
        const remainingText = diff <= 0 ? 'soon' : `in ${remainingDuration}`;

        console.warn(
          `Session ending notification: The session will be closed ${remainingText}. Save your work to prevent lost changes.`
        );

        // You can integrate with your notification system here
        // For example, if using Material-UI Snackbar or similar

        setNotificationSent(true);
        clearInterval(timer);
      }
    }, 1000);

    // eslint-disable-next-line consistent-return
    return () => {
      clearInterval(timer);
    };
  }, [sessionId, sessionStartTime, maxTtl, notificationTime, notificationSent]);
}
